import { router as inertiaRouter, usePage } from '@inertiajs/vue3'

/**
 * Drops partial reloads started by a page that another visit is already
 * replacing.
 *
 * A partial reload (`only`, `except` or `reset`) gets back only the props it
 * asked for, so shared props such as `authUser` are left out. Inertia merges
 * that answer into the current props only while its page component is still
 * the one on screen. A visit to another page swaps that page in only once its
 * component chunk has loaded, so for a moment after the visit's response the
 * old page is still on screen and can be clicked. A partial reload started in
 * that moment (a filter preset, a sort) is answered after the new page is in,
 * is not merged, and puts the old page back without `authUser`, and its layout
 * throws reading `authUser.authorizations`.
 *
 * `beforeUpdate` fires just before a visit's page starts swapping in. Until
 * that page is on screen, partial reloads are dropped: the page asking for
 * them is on its way out.
 */
export function dropStalePartialReloads({
  router = inertiaRouter,
  currentComponent = () => usePage().component,
} = {}) {
  let incomingComponent = null

  router.on('beforeUpdate', (event) => {
    const { component } = event.detail.page

    incomingComponent = component === currentComponent() ? null : component
  })

  // A history visit can replace the page without a `beforeUpdate`, so the
  // swap that was waiting may never land.
  router.on('navigate', () => {
    incomingComponent = null
  })

  router.on('before', (event) => {
    if (!incomingComponent || !isPartialReload(event.detail.visit)) {
      return
    }

    if (incomingComponent === currentComponent()) {
      incomingComponent = null

      return
    }

    return false
  })
}

function isPartialReload(visit) {
  return (
    (visit.only?.length ?? 0) > 0 ||
    (visit.except?.length ?? 0) > 0 ||
    (visit.reset?.length ?? 0) > 0
  )
}
